// src/features/chales/components/EditReservationModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import toast from "react-hot-toast";
import { formatDateToYYYYMMDD } from "utils/dateUtils";
import { getAvailableChalesForPeriod } from "../hooks/useChalesData";
import { CalendarCheck } from "lucide-react";
import { checkHoliday } from "services/holidayService";
import { useClients } from "features/clients/hooks/useClients";

function EditReservationModal({
  isOpen,
  onClose,
  onUpdateReservation,
  reservationData,
}) {
  const [availableChales, setAvailableChales] = useState([]);
  const [isChecking, setIsChecking] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
    setValue,
  } = useForm();

  const { addClient } = useClients();

  const checkInValue = watch("checkInDate");
  const checkOutValue = watch("checkOutDate");
  const chaletNumberValue = watch("chaletNumber");

  const holidayNameCheckIn = useMemo(() => {
    if (!checkInValue) return null;
    const [year, month, day] = checkInValue.split("-").map(Number);
    if (!year || !month || !day) return null;
    const dateObj = new Date(year, month - 1, day);
    return checkHoliday(dateObj);
  }, [checkInValue]);

  useEffect(() => {
    if (reservationData) {
      reset({
        guestInfo: reservationData.guestInfo,
        phoneInfo: reservationData.phoneInfo,
        chaletNumber: String(reservationData.chaletNumber),
        checkInDate: formatDateToYYYYMMDD(reservationData.checkInDate),
        checkOutDate: formatDateToYYYYMMDD(reservationData.checkOutDate),
        depositDueDate: reservationData.depositDueDate
          ? formatDateToYYYYMMDD(reservationData.depositDueDate)
          : "",
        observations: reservationData.observations || "",
      });
    }
  }, [reservationData, reset]);

  useEffect(() => {
    const checkAvailability = async () => {
      if (checkInValue && checkOutValue) {
        setIsChecking(true);
        const checkInDate = new Date(checkInValue + "T00:00:00");
        const checkOutDate = new Date(checkOutValue + "T00:00:00");

        if (checkOutDate > checkInDate) {
          const available = await getAvailableChalesForPeriod(
            checkInDate,
            checkOutDate,
            reservationData.reservationId
          );
          setAvailableChales(available);

          if (
            chaletNumberValue &&
            !available.includes(Number(chaletNumberValue))
          ) {
            if (!available.includes(Number(chaletNumberValue))) {
              setAvailableChales((prev) =>
                [...prev, Number(chaletNumberValue)].sort((a, b) => a - b)
              );
            }
            toast.error(
              `Atenção: O Chalé ${chaletNumberValue} pode ter um conflito de datas.`
            );
          }
        } else {
          setAvailableChales([]);
        }
        setIsChecking(false);
      }
    };
    if (isOpen) {
      checkAvailability();
    }
  }, [
    checkInValue,
    checkOutValue,
    reservationData,
    setValue,
    isOpen,
    chaletNumberValue,
  ]);

  const onSubmit = async (data) => {
    const checkIn = new Date(data.checkInDate + "T00:00:00");
    const checkOut = new Date(data.checkOutDate + "T00:00:00");

    if (checkOut <= checkIn) {
      toast.error("A data de Check-out deve ser posterior à de Check-in.");
      return;
    }
    if (!data.chaletNumber) {
      toast.error("Por favor, selecione um chalé.");
      return;
    }

    await addClient({
      name: data.guestInfo,
      phone: data.phoneInfo,
      source: "reservation_edit",
    });

    const depositDueDate = data.depositDueDate
      ? new Date(data.depositDueDate + "T00:00:00")
      : null;

    onUpdateReservation(reservationData.reservationId, {
      guestInfo: data.guestInfo,
      phoneInfo: data.phoneInfo,
      chaletNumber: parseInt(data.chaletNumber, 10),
      checkInDate: checkIn,
      checkOutDate: checkOut,
      depositDueDate: depositDueDate,
      observations: data.observations || "",
    });
    onClose();
  };

  if (!isOpen) return null;

  const allChalets = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[85vh] sm:max-w-[500px] p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-slate-100 dark:border-border shrink-0">
          <DialogTitle>Editar Reserva</DialogTitle>
        </DialogHeader>

        <div className="px-6 py-6 overflow-y-auto flex-1">
          <form id="edit-reservation-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8 pb-12">
            {/* -- SEÇÃO: PERÍODO -- */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center">
                Período
              </h3>
              <div className="flex flex-wrap gap-4">
                <div className="space-y-1 flex flex-col w-fit">
                  <Label htmlFor="checkInDate">Check-in</Label>
                  <Input
                    type="date"
                    id="checkInDate"
                    {...register("checkInDate", { required: true })}
                    className="force-light-datepicker w-full min-w-[160px]"
                  />
                  {holidayNameCheckIn && (
                    <div className="mt-1 flex items-center gap-1 bg-purple-100 text-purple-800 text-xs font-bold px-2 py-1 rounded border border-purple-200 whitespace-nowrap w-full">
                      <CalendarCheck className="h-3 w-3 flex-shrink-0" />
                      {holidayNameCheckIn}
                    </div>
                  )}
                </div>

                <div className="space-y-1 flex flex-col w-fit">
                  <Label htmlFor="checkOutDate">Check-out</Label>
                  <Input
                    type="date"
                    id="checkOutDate"
                    {...register("checkOutDate", { required: true })}
                    className="force-light-datepicker w-full min-w-[160px]"
                  />
                </div>
              </div>
            </div>

            {/* -- SEÇÃO: CHALÉS -- */}
            <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-border">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center mt-4">
                Chalé
              </h3>

              {isChecking ? (
                <p className="text-sm text-muted-foreground">
                  Verificando disponibilidade...
                </p>
              ) : (
                <Controller
                  name="chaletNumber"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="grid grid-cols-5 gap-2">
                      {allChalets.map((num) => {
                        const numStr = String(num);
                        const isAvailable = availableChales.includes(num);
                        const isCurrent = numStr === field.value;
                        const isSelectable =
                          isAvailable ||
                          num === Number(reservationData.chaletNumber);

                        return (
                          <button
                            key={num}
                            type="button"
                            disabled={!isSelectable}
                            onClick={() => {
                              if (!isSelectable) return;
                              field.onChange(numStr);
                            }}
                            className={`
                              min-h-[44px] w-full rounded-md text-sm font-medium transition-colors border
                              ${
                                !isSelectable
                                  ? "bg-muted text-muted-foreground opacity-50 cursor-not-allowed border-transparent"
                                  : isCurrent
                                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                  : "bg-background hover:bg-accent hover:text-accent-foreground border-input"
                              }
                            `}
                          >
                            {num < 10 ? `0${num}` : num}
                          </button>
                        );
                      })}
                    </div>
                  )}
                />
              )}
            </div>

            {/* -- SEÇÃO: HÓSPEDE -- */}
            <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-border">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center mt-4">
                Dados do Hóspede
              </h3>

              <div className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="guestInfo">Nome Completo</Label>
                  <Input
                    id="guestInfo"
                    {...register("guestInfo", {
                      required: "Nome é obrigatório",
                    })}
                    placeholder="Nome do hóspede..."
                  />
                  {errors.guestInfo && (
                    <p className="text-xs text-destructive">
                      {errors.guestInfo.message}
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="phoneInfo">Telefone</Label>
                  <Input
                    id="phoneInfo"
                    {...register("phoneInfo")}
                    placeholder="(99) 99999-9999"
                  />
                </div>
              </div>
            </div>

            {/* -- SEÇÃO: DETALHES -- */}
            <div className="space-y-4 pt-2 border-t border-slate-100 dark:border-border">
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider flex items-center mt-4">
                Detalhes
              </h3>

              <div className="space-y-4">
                <div className="space-y-1 flex flex-col w-fit">
                  <Label htmlFor="depositDueDate">Vencimento do Depósito</Label>
                  <Input
                    type="date"
                    id="depositDueDate"
                    {...register("depositDueDate")}
                    className="force-light-datepicker w-full sm:w-auto min-w-[160px]"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="observations">Observações</Label>
                  <Textarea
                    id="observations"
                    {...register("observations")}
                    className="resize-none"
                    placeholder="Digite aqui suas observações..."
                    rows={2}
                  />
                </div>
              </div>
            </div>

          </form>
        </div>
        
        <DialogFooter className="px-6 py-4 border-t border-slate-100 dark:border-border bg-slate-50 dark:bg-card shrink-0">
          <Button form="edit-reservation-form" type="submit" size="lg" className="w-full sm:w-auto min-h-[44px]">
            Salvar Alterações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditReservationModal;
